import { COURSES as REACT_COURSES } from '@/data/courses/index';
import { SQL_COURSES, SQL_ADVANCED_PHASE_COURSES } from '@/data/sql';
import { SQL_PHASE1_COURSES } from '@/data/sql/phase1';
import { PYTHON_COURSES } from '@/data/pythonCourseData';
import { AI_COURSES } from '@/data/ai';
import { JAVA_COURSES } from '@/data/java';
import { DOTNET_COURSES } from '@/data/dotnet';
import { CSHARP_COURSES } from '@/data/csharp';
import { AWS_COURSES } from '@/data/aws';
import { AZURE_COURSES } from '@/data/azure';
import { JAVASCRIPT_COURSES } from '@/data/javascript';
import { DATASCIENCE_COURSES } from '@/data/datascience';
import { DATAENGINEERING_COURSES } from '@/data/dataengineering';
import { ETL_COURSES } from '@/data/etl';
import { GCP_COURSES } from '@/data/gcp';
import { DEVOPS_COURSES } from '@/data/devops';
import { APPSEC_COURSES } from '@/data/appsec';
import { INFRASTRUCTURE_COURSES } from '@/data/infrastructure';
import { FRONTEND_ADVANCED_COURSES } from '@/data/frontend-advanced';
import { TESTING_MODERN_COURSES } from '@/data/testing-modern';
import { SELENIUM_COURSES } from '@/data/testing';
import { WEB3_DEVELOPMENT_COURSES } from '@/data/web3-development';
import { SERVERLESS_EDGE_COURSES } from '@/data/serverless-edge';
import { SPECIALIZED_AI_COURSES } from '@/data/specialized-ai';
import { BookOpen, Database, Code, Brain, Coffee, Code2, Cloud, BarChart3, Shield, Link, Gamepad2, Palette, Users, TrendingUp, Monitor, Settings, Layers, TestTube, Cpu, Bot, GitBranch, Smartphone } from 'lucide-react';
import { AssignedCourse } from '@/hooks/useUserCourseAssignments';
import { POWERBI_COURSES } from '@/data/powerbi';
import { TABLEAU_COURSES } from '@/data/tableau';
import { MOBILE_COURSES } from '@/data/mobile';
import { BACKEND_COURSES } from '@/data/backend';
import { DATABASE_COURSES } from '@/data/database';
import { SECURITY_COURSES } from '@/data/security';
import { BLOCKCHAIN_COURSES } from '@/data/blockchain';
import { RPA_COURSES } from '@/data/rpa';
import { GRAPHICS_COURSES } from '@/data/graphics';
import { DATA_SCIENCE_COURSES } from '@/data/data-science';
import { UNITY_GAME_DEVELOPMENT, UNREAL_GAME_DEVELOPMENT, AR_VR_DEVELOPMENT, DESKTOP_APPLICATION_DEVELOPMENT } from '@/data/game-development';
import { UI_UX_COURSES } from '@/data/ui-ux-design';
import { GRAPHICS_PROGRAMMING_COURSES } from '@/data/graphics-programming';
import { ENTERPRISE_TRAINING_COURSES } from '@/data/enterprise-training';
import { PROFESSIONAL_DEVELOPMENT_COURSES } from '@/data/professional-development';
import { AUTOMATION_COURSES } from '@/data/automation';
import { NO_CODE_COURSES } from '@/data/no-code';
import { RUST_COURSES } from '@/data/rust';
import { EDGE_COMPUTING_COURSES } from '@/data/edge-computing';
import { GREEN_TECH_COURSES } from '@/data/green-tech';
import { CROSS_PLATFORM_COURSES } from '@/data/cross-platform';
import { MODERN_FRONTEND_COURSES } from '@/data/modern-frontend';
import { ADVANCED_API_COURSES } from '@/data/advanced-api';
import { KUBERNETES_MASTERY_COURSES } from '@/data/kubernetes-mastery';
import { Zap, Wrench, Shield as RustIcon, Globe, Leaf, Grid3x3, Layers2, Webhook, Ship } from 'lucide-react';

export interface CourseCategory {
  id: string;
  title: string;
  icon: any;
  route: string;
  description: string;
  courses: CourseItem[];
}

export interface CourseItem {
  id: string;
  title: string;
  level: string;
  duration: string;
  route: string;
  description: string;
}

// Helper function to get course level with fallbacks
const getCourseLevel = (course: any): string => {
  return course.level || course.difficulty || 'beginner';
};

// Helper function to get course duration with fallbacks
const getCourseDuration = (course: any): string => {
  return course.totalDuration || 
         course.duration || 
         (course.durationHours ? `${course.durationHours} hours` : '') ||
         (course.estimatedHours ? `${course.estimatedHours} hours` : '') ||
         '2 hours';
};

// Build all course categories with their courses - React Development now separate from All Courses
export const ALL_COURSE_CATEGORIES: CourseCategory[] = [
  {
    id: 'react',
    title: 'React Development',
    icon: Code,
    route: '/react-courses',
    description: 'Modern React development with hooks, components, and state management',
    courses: REACT_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'tableau',
    title: 'Tableau Courses',
    icon: BarChart3,
    route: '/tableau-courses',
    description: 'Data visualization & business intelligence with Tableau',
    courses: TABLEAU_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/tableau-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'datascience',
    title: 'Data Science Courses',
    icon: BarChart3,
    route: '/datascience-courses',
    description: 'Data analysis, ML & business analytics',
    courses: DATASCIENCE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/datascience-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'dataengineering',
    title: 'Data Engineering Courses',
    icon: Database,
    route: '/dataengineering-courses',
    description: 'Build scalable data infrastructure & pipelines',
    courses: DATAENGINEERING_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/dataengineering-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'etl',
    title: 'ETL Developer Courses',
    icon: GitBranch,
    route: '/etl-courses',
    description: 'Master ETL tools, data transformation, and pipeline automation',
    courses: ETL_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/etl-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'javascript',
    title: 'JavaScript Courses',
    icon: Code,
    route: '/javascript-courses',
    description: 'Core JavaScript programming',
    courses: JAVASCRIPT_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/javascript-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'gcp',
    title: 'Google Cloud Platform',
    icon: Cloud,
    route: '/gcp-courses',
    description: 'Google Cloud Platform & cloud architecture',
    courses: GCP_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/gcp-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'aws',
    title: 'AWS Courses',
    icon: Cloud,
    route: '/aws-courses',
    description: 'Amazon Web Services & cloud architecture',
    courses: AWS_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/aws-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'azure',
    title: 'Azure Courses',
    icon: Cloud,
    route: '/azure-courses',
    description: 'Microsoft Azure & cloud architecture',
    courses: AZURE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/azure-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'dotnet',
    title: '.NET Courses',
    icon: Code2,
    route: '/dotnet-courses',
    description: 'Enterprise .NET development',
    courses: [...DOTNET_COURSES, ...CSHARP_COURSES].map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/dotnet-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'python',
    title: 'Python Courses',
    icon: Code,
    route: '/python-courses',
    description: 'Python & automation',
    courses: PYTHON_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/python-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'sql',
    title: 'SQL Courses',
    icon: Database,
    route: '/sql-courses',
    description: 'Database design & queries',
    courses: [...SQL_COURSES, ...SQL_PHASE1_COURSES, ...SQL_ADVANCED_PHASE_COURSES].map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/sql-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'devops',
    title: 'DevOps Courses',
    icon: Cloud,
    route: '/devops-courses',
    description: 'DevOps, CI/CD, Docker & Kubernetes',
    courses: DEVOPS_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/devops-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'appsec',
    title: 'Application Security',
    icon: Code,
    route: '/appsec-courses',
    description: 'Application security & OWASP best practices',
    courses: APPSEC_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/appsec-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'ai',
    title: 'AI & ML',
    icon: Brain,
    route: '/ai-courses',
    description: 'Machine Learning',
    courses: AI_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/ai-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'powerbi',
    title: 'Power BI Courses',
    icon: BarChart3,
    route: '/powerbi-courses',
    description: 'Microsoft Power BI & business intelligence',
    courses: POWERBI_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/powerbi-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'java',
    title: 'Java Courses',
    icon: Coffee,
    route: '/java-courses',
    description: 'Java development',
    courses: JAVA_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/java-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'mobile',
    title: 'Mobile Development',
    icon: Smartphone,
    route: '/mobile-courses',
    description: 'Cross-platform mobile app development',
    courses: MOBILE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: course.level,
      duration: course.totalDuration,
      route: `/mobile-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'backend',
    title: 'Backend Development', 
    icon: Code,
    route: '/backend-courses',
    description: 'Server-side development and APIs',
    courses: BACKEND_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: course.level,
      duration: course.totalDuration,
      route: `/backend-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'database',
    title: 'Database Technologies',
    icon: Database,
    route: '/database-courses',
    description: 'Database design and management',
    courses: DATABASE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: course.level,
      duration: course.totalDuration,
      route: `/database-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'security',
    title: 'Security Courses',
    icon: Shield,
    route: '/security-courses',
    description: 'Cybersecurity, application security & cloud security',
    courses: SECURITY_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/security-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'blockchain',
    title: 'Blockchain Courses',
    icon: Link,
    route: '/blockchain-courses',
    description: 'Blockchain development, smart contracts & DeFi',
    courses: BLOCKCHAIN_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/blockchain-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'game-development',
    title: 'Game Development',
    icon: Gamepad2,
    route: '/game-development-courses',
    description: 'Unity, Unreal Engine, AR/VR, and desktop game development',
    courses: [
      {
        id: UNITY_GAME_DEVELOPMENT.id,
        title: UNITY_GAME_DEVELOPMENT.title,
        level: getCourseLevel(UNITY_GAME_DEVELOPMENT),
        duration: getCourseDuration(UNITY_GAME_DEVELOPMENT),
        route: `/game-development-courses/${UNITY_GAME_DEVELOPMENT.id}`,
        description: UNITY_GAME_DEVELOPMENT.description
      },
      {
        id: UNREAL_GAME_DEVELOPMENT.id,
        title: UNREAL_GAME_DEVELOPMENT.title,
        level: getCourseLevel(UNREAL_GAME_DEVELOPMENT),
        duration: getCourseDuration(UNREAL_GAME_DEVELOPMENT),
        route: `/game-development-courses/${UNREAL_GAME_DEVELOPMENT.id}`,
        description: UNREAL_GAME_DEVELOPMENT.description
      },
      {
        id: AR_VR_DEVELOPMENT.id,
        title: AR_VR_DEVELOPMENT.title,
        level: getCourseLevel(AR_VR_DEVELOPMENT),
        duration: getCourseDuration(AR_VR_DEVELOPMENT),
        route: `/game-development-courses/${AR_VR_DEVELOPMENT.id}`,
        description: AR_VR_DEVELOPMENT.description
      },
      {
        id: DESKTOP_APPLICATION_DEVELOPMENT.id,
        title: DESKTOP_APPLICATION_DEVELOPMENT.title,
        level: getCourseLevel(DESKTOP_APPLICATION_DEVELOPMENT),
        duration: getCourseDuration(DESKTOP_APPLICATION_DEVELOPMENT),
        route: `/game-development-courses/${DESKTOP_APPLICATION_DEVELOPMENT.id}`,
        description: DESKTOP_APPLICATION_DEVELOPMENT.description
      }
    ]
  },
  {
    id: 'ui-ux-design',
    title: 'UI/UX Design',
    icon: Palette,
    route: '/ui-ux-courses',
    description: 'User interface and user experience design',
    courses: UI_UX_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/ui-ux-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'graphics-programming',
    title: 'Graphics & WebGL Programming',
    icon: Monitor,
    route: '/graphics-courses',
    description: 'WebGL, 3D graphics, game development, and visual programming',
    courses: [...GRAPHICS_PROGRAMMING_COURSES, ...GRAPHICS_COURSES].map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/graphics-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'enterprise-training',
    title: 'Enterprise Training',
    icon: Users,
    route: '/enterprise-courses',
    description: 'Technical leadership, system design & enterprise development',
    courses: ENTERPRISE_TRAINING_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: course.level,
      duration: course.totalDuration,
      route: `/enterprise-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'professional-development',
    title: 'Professional Development',
    icon: TrendingUp,
    route: '/professional-development-courses',
    description: 'Communication, career advancement & productivity skills',
    courses: PROFESSIONAL_DEVELOPMENT_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: course.level,
      duration: course.totalDuration,
      route: `/professional-development-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'infrastructure',
    title: 'Infrastructure as Code',
    icon: Settings,
    route: '/infrastructure-courses',
    description: 'Infrastructure automation, Terraform, and configuration management',
    courses: INFRASTRUCTURE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/infrastructure-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'frontend-advanced',
    title: 'Advanced Frontend Development',
    icon: Layers,
    route: '/frontend-advanced-courses',
    description: 'Modern frontend frameworks, performance optimization, and advanced patterns',
    courses: FRONTEND_ADVANCED_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/frontend-advanced-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'testing-modern',
    title: 'Modern Testing & Quality Assurance',
    icon: TestTube,
    route: '/testing-modern-courses',
    description: 'Testing frameworks, automation, and quality engineering',
    courses: [...TESTING_MODERN_COURSES, ...SELENIUM_COURSES].map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/testing-modern-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'web3-development',
    title: 'Web3 & Blockchain Development',
    icon: Link,
    route: '/web3-development-courses',
    description: 'Web3 technologies, smart contracts, and decentralized applications',
    courses: WEB3_DEVELOPMENT_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/web3-development-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'serverless-edge',
    title: 'Serverless & Edge Computing',
    icon: Cpu,
    route: '/serverless-edge-courses',
    description: 'Serverless architecture, edge computing, and distributed systems',
    courses: SERVERLESS_EDGE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/serverless-edge-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'mlops',
    title: 'MLOps & AI Deployment',
    icon: Brain,
    route: '/mlops-courses',
    description: 'Advanced machine learning operations, model deployment, and AI infrastructure',
    courses: DATA_SCIENCE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/mlops-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'rpa-tools',
    title: 'RPA Tools & Automation',
    icon: Bot,
    route: '/rpa-tools-courses',
    description: 'Master robotic process automation with leading RPA platforms',
    courses: [
      {
        id: 'uipath-fundamentals',
        title: 'UiPath Fundamentals & Studio Basics',
        level: 'beginner',
        duration: '12 hours',
        route: '/rpa-tools-courses/uipath-fundamentals',
        description: 'Master the fundamentals of UiPath Studio and create automation workflows'
      },
      {
        id: 'power-automate-desktop-fundamentals',
        title: 'Power Automate Desktop Fundamentals',
        level: 'beginner',
        duration: '10 hours',
        route: '/rpa-tools-courses/power-automate-desktop-fundamentals',
        description: 'Master Microsoft Power Automate Desktop for Windows automation'
      },
      {
        id: 'automation-anywhere-development',
        title: 'Automation Anywhere Bot Development',
        level: 'intermediate',
        duration: '13 hours',
        route: '/rpa-tools-courses/automation-anywhere-development',
        description: 'Master Automation Anywhere A360 platform for enterprise RPA development'
      },
      {
        id: 'rpa-finance-accounting',
        title: 'RPA for Finance & Accounting',
        level: 'intermediate',
        duration: '12 hours',
        route: '/rpa-tools-courses/rpa-finance-accounting',
        description: 'Implement RPA solutions for financial processes and compliance'
      }
    ]
  },
  {
    id: 'specialized-ai',
    title: 'Specialized AI Platforms',
    icon: Brain,
    route: '/specialized-ai-courses',
    description: 'Advanced AI platform integration - NVIDIA, Google Gemini, GitHub Copilot',
    courses: SPECIALIZED_AI_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/specialized-ai-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'automation',
    title: 'Automation & Workflow',
    icon: Zap,
    route: '/automation-courses',
    description: 'Visual workflow automation with n8n, Zapier, and Make',
    courses: AUTOMATION_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/automation-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'no-code',
    title: 'No-Code Development',
    icon: Wrench,
    route: '/no-code-courses',
    description: 'Build applications without coding - Power Platform, bubble.io, Webflow',
    courses: NO_CODE_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/no-code-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'rust',
    title: 'Rust Programming',
    icon: RustIcon,
    route: '/rust-courses',
    description: 'Systems programming with memory safety and high performance',
    courses: RUST_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/rust-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'edge-computing',
    title: 'Edge Computing',
    icon: Globe,
    route: '/edge-computing-courses',
    description: 'CDN, Edge Functions, Cloudflare Workers, and distributed computing',
    courses: EDGE_COMPUTING_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/edge-computing-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'green-tech',
    title: 'Green Tech & Sustainability',
    icon: Leaf,
    route: '/green-tech-courses',
    description: 'Sustainable software engineering and carbon-efficient computing',
    courses: GREEN_TECH_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/green-tech-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'cross-platform',
    title: 'Cross-Platform Development',
    icon: Grid3x3,
    route: '/cross-platform-courses',
    description: 'Flutter, React Native, Electron - build once, deploy everywhere',
    courses: CROSS_PLATFORM_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/cross-platform-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'modern-frontend',
    title: 'Modern Frontend Frameworks',
    icon: Layers2,
    route: '/modern-frontend-courses',
    description: 'Svelte, SvelteKit, Solid.js, and next-generation frameworks',
    courses: MODERN_FRONTEND_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/modern-frontend-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'advanced-api',
    title: 'Advanced API Development',
    icon: Webhook,
    route: '/advanced-api-courses',
    description: 'GraphQL Federation, gRPC, and high-performance API patterns',
    courses: ADVANCED_API_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/advanced-api-courses/${course.id}`,
      description: course.description
    }))
  },
  {
    id: 'kubernetes-mastery',
    title: 'Kubernetes Mastery',
    icon: Ship,
    route: '/kubernetes-mastery-courses',
    description: 'Container orchestration, CKA/CKAD certification, and production deployments',
    courses: KUBERNETES_MASTERY_COURSES.map(course => ({
      id: course.id,
      title: course.title,
      level: getCourseLevel(course),
      duration: getCourseDuration(course),
      route: `/kubernetes-mastery-courses/${course.id}`,
      description: course.description
    }))
  }
];

export const filterCoursesByAssignments = (
  assignments: AssignedCourse[],
  hasAdminAccess: boolean
): CourseCategory[] => {
  if (hasAdminAccess) {
    return ALL_COURSE_CATEGORIES;
  }

  return ALL_COURSE_CATEGORIES
    .map(category => {
      // Filter courses based on assignments
      const assignedCourses = category.courses.filter(course =>
        assignments.some(assignment => assignment.course_id === course.id)
      );

      return {
        ...category,
        courses: assignedCourses
      };
    })
    .filter(category => category.courses.length > 0); // Only show categories with assigned courses
};

// Get total course count for a category
export const getCategoryCourseCount = (
  categoryId: string,
  assignments: AssignedCourse[],
  hasAdminAccess: boolean
): string => {
  if (hasAdminAccess) {
    const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
    return category ? category.courses.length.toString() : '0';
  }

  const assignedCount = assignments.filter(assignment => {
    const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
    return category?.courses.some(course => course.id === assignment.course_id);
  }).length;

  return assignedCount.toString();
};
